import React, { useState, useEffect, useContext, useRef } from 'react';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import { SnackbarContext } from './userContext';
import { BackDropLoad } from './LoadingBackDrop';
import { AutoCompLoc } from './AutoCompLoc';
import { PageContainer } from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

const subjects = [
  'Registration',
  'Subscription',
  'Real Estates',
  'Info in EngInfo',
  'Contacting a Supplier / Service Provider / Engineer / Workforce',
  'Others',
];

/* ─────────── inline icons ─────────── */

const I = {
  Person: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Phone: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72a2 2 0 0 1 1.72 2z" />
    </svg>
  ),
  Mail: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <path d="m22 6-10 7L2 6" />
    </svg>
  ),
  Clock: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  ArrowRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  ChevronDown: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="m6 9 6 6 6-6" />
    </svg>
  ),
  Check: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12l5 5L20 7" />
    </svg>
  ),
  Spark: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="m12 3 2 7 7 2-7 2-2 7-2-7-7-2 7-2z" />
    </svg>
  ),
  ShieldCheck: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  ),
  Headset: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M3 14v-2a9 9 0 0 1 18 0v2" />
      <path d="M21 14v3a3 3 0 0 1-3 3h-1v-7h1a3 3 0 0 1 3 3z" />
      <path d="M3 14v3a3 3 0 0 0 3 3h1v-7H6a3 3 0 0 0-3 3z" />
    </svg>
  ),
  Bolt: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  Lock: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
};

/* ─────────── Field ─────────── */

function Field({ label, value, onChange, error, errorMsg, multiline, rows = 4, required, type = 'text', icon, autoComplete, inputMode, maxLength, placeholder }) {
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <div className="w-full">
      <label className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.14em] text-fg-tertiary">
        {label}
        {required && <span className="ml-1 text-amber">*</span>}
      </label>
      <div
        className={`
          relative flex gap-2 rounded-lg
          ${multiline ? 'min-h-[120px] items-start py-3 px-3' : 'h-11 items-center px-3'}
          bg-surface-overlay/70
          border
          transition-[border-color,background-color,box-shadow] duration-200 ease-out-soft
          focus-within:bg-surface-overlay
          focus-within:shadow-[0_0_0_3px_rgba(251,191,36,0.22)]
          ${error
            ? 'border-red-400/55 focus-within:border-red-400'
            : 'border-amber/22 hover:border-amber/55 focus-within:border-amber'}
        `}
      >
        {icon && !multiline && (
          <span className={`shrink-0 ${error ? 'text-red-400' : 'text-amber/80'}`}>{icon}</span>
        )}
        <Tag
          type={type}
          value={value}
          onChange={onChange}
          rows={multiline ? rows : undefined}
          autoComplete={autoComplete}
          inputMode={inputMode}
          maxLength={maxLength}
          placeholder={placeholder}
          className={`
            w-full bg-transparent
            text-[14px] font-medium text-fg-primary
            placeholder:text-fg-muted
            outline-none border-0 p-0
            ${multiline ? 'leading-[1.6] resize-y min-h-[100px]' : 'leading-none'}
          `}
        />
      </div>
      <div className="mt-1.5 min-h-[14px] text-[11px] leading-[1.4]">
        {error && <span className="text-red-400">{errorMsg}</span>}
      </div>
    </div>
  );
}

/* ─────────── Custom Select (matches Contact Us dropdown pattern) ─────────── */

function Select({ label, value, onChange, error, errorMsg, required, options, placeholder = 'Select…' }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); btnRef.current && btnRef.current.focus(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, options.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
      else if (e.key === 'Enter') { e.preventDefault(); pick(options[active]); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active, options]);

  const pick = (opt) => {
    if (!opt) return;
    onChange({ target: { value: opt.value } });
    setOpen(false);
    btnRef.current && btnRef.current.focus();
  };

  const selected = options.find((o) => o.value === value);
  const isPlaceholder = !selected || selected.value === '0';
  const display = !isPlaceholder ? selected.label : (selected ? selected.label : placeholder);

  return (
    <div ref={ref} className="relative w-full">
      <label className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.14em] text-fg-tertiary">
        {label}
        {required && <span className="ml-1 text-amber">*</span>}
      </label>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`
          relative flex h-11 w-full items-center gap-2 rounded-lg pl-3 pr-9 text-left
          bg-surface-overlay/70
          border
          transition-[border-color,background-color,box-shadow] duration-200 ease-out-soft
          ${error
            ? 'border-red-400/55'
            : open
              ? 'border-amber bg-surface-overlay shadow-[0_0_0_3px_rgba(251,191,36,0.22)]'
              : 'border-amber/22 hover:border-amber/55'}
        `}
      >
        <span className={`flex-1 truncate text-[14px] font-medium leading-none ${isPlaceholder ? 'text-fg-muted' : 'text-fg-primary'}`}>
          {display}
        </span>
        <span className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transition-[color,transform] duration-200 ${open ? 'rotate-180 text-amber' : 'text-amber/80'}`}>
          <I.ChevronDown className="h-4 w-4" />
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          className="
            absolute left-0 right-0 top-full z-50 mt-2
            max-h-72 overflow-auto rounded-xl
            border border-amber/30 bg-surface-elevated
            shadow-dialog
            animate-[fadeIn_140ms_ease-out]
            py-1
          "
        >
          {options.map((opt, i) => {
            const isSel = opt.value === value;
            const isPlace = opt.value === '0';
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSel}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(opt)}
                className={`
                  flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-[13.5px]
                  transition-[background-color,color] duration-150 ease-out-soft
                  ${isSel
                    ? 'bg-amber/14 text-amber font-semibold'
                    : i === active
                      ? 'bg-amber/8 text-fg-primary'
                      : isPlace ? 'text-fg-muted' : 'text-fg-secondary hover:bg-amber/8 hover:text-fg-primary'}
                `}
              >
                <span className="flex-1 truncate">{opt.label}</span>
                {isSel && <I.Check className="h-3.5 w-3.5 text-amber" />}
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-1.5 min-h-[14px] text-[11px] leading-[1.4]">
        {error && <span className="text-red-400">{errorMsg}</span>}
      </div>
    </div>
  );
}

/* ═══════════════════ ContactNow ═══════════════════ */

export const ContactNow = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mob, setMob] = useState('');
  const [sub, setSub] = useState('0');
  const [msg, setMsg] = useState('');
  const [wait, setWait] = useState(false);
  const [loc, setLoc] = useState('');
  const [error, setError] = useState(0);
  const [metadata, setMetadata] = useState(null);
  const [sent, setSent] = useState(false);
  const { setSnack } = useContext(SnackbarContext);

  useEffect(() => {
    window.scrollTo(0, 0);
    Axios.post('/new_apis/getsinglemetadata', { type: 'contact' }, { headers: { key } }).then((res) => {
      if (res.data.status) setMetadata(res.data.metadata[0]);
      else setMetadata({ seo_title: 'Contact Us | ESAY' });
    });
  }, []);

  const reset = () => {
    setName(''); setEmail(''); setMob(''); setSub('0'); setMsg(''); setLoc('');
    setSent(false); setError(0);
  };

  const submit = (e) => {
    e.preventDefault();
    setError(0);
    if (name === '') {
      setError(1);
      setSnack({ message: 'Please enter your name.', type: 'error', open: true });
      return;
    }
    if (mob.length !== 10) {
      setError(2);
      setSnack({ message: 'Enter a valid 10-digit mobile number.', type: 'error', open: true });
      return;
    }
    if (!loc) {
      setError(5);
      setSnack({ message: 'Please give your location.', type: 'error', open: true });
      return;
    }
    if (sub === '0') {
      setError(3);
      setSnack({ message: 'Please select your subject.', type: 'error', open: true });
      return;
    }
    if (msg === '') {
      setError(4);
      setSnack({ message: 'Please enter your message.', type: 'error', open: true });
      return;
    }

    setWait(true);
    Axios.post('/new_apis/contactnow', { name, email, mob, loc, sub, msg }, { headers: { key } }).then((res) => {
      setWait(false);
      if (res.data.status) {
        setSent(true);
      }
      setSnack({
        message: res.data.msg,
        type: res.data.status ? 'success' : 'error',
        open: true,
      });
    }).catch(() => {
      setWait(false);
      setSnack({ message: 'Network error. Please try again.', type: 'error', open: true });
    });
  };

  /* Success state */
  if (sent) {
    return (
      <>
        {metadata && (
          <Helmet>
            <title>{metadata.seo_title}</title>
          </Helmet>
        )}
        <PageContainer maxWidth="md">
          <div className="flex min-h-[60vh] items-center justify-center py-10">
            <article
              className="
                relative w-full max-w-[520px] overflow-hidden text-center
                rounded-3xl border border-amber/40 bg-surface-paper
                px-6 py-10 sm:px-10 sm:py-14
                shadow-[0_24px_72px_-24px_rgba(0,0,0,0.6),0_0_72px_-12px_rgba(251,191,36,0.25)]
              "
            >
              <span aria-hidden className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-amber to-transparent" />
              <div
                aria-hidden
                className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-56 w-56 rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.22), transparent 65%)' }}
              />
              <div className="relative mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-amber/45 bg-gradient-to-br from-amber/30 to-amber/10 text-amber shadow-[0_0_24px_-4px_rgba(251,191,36,0.4)]">
                <I.Check className="h-7 w-7" />
              </div>
              <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-amber/30 bg-amber/10 px-3 py-1">
                <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-amber animate-pulse" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Message sent</span>
              </div>
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-[-0.018em] text-fg-primary">
                We've got your message
              </h1>
              <div aria-hidden className="mx-auto mt-3 h-[2px] w-12 rounded-full bg-gradient-to-r from-transparent via-amber to-transparent" />
              <p className="mx-auto mt-3 max-w-[380px] text-[13.5px] leading-[1.65] text-fg-secondary">
                Thanks for reaching out. Our team typically responds within{' '}
                <strong className="font-bold text-amber">1–2 business days</strong>.
              </p>
              <button
                type="button"
                onClick={reset}
                className="
                  group mt-5 inline-flex h-11 items-center justify-center gap-1.5 rounded-lg px-5
                  text-[12px] font-bold uppercase tracking-[0.1em]
                  bg-amber text-ink
                  transition-[background-color,box-shadow,transform] duration-200 ease-out-soft
                  hover:bg-amber-light
                  hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_8px_22px_-8px_rgba(251,191,36,0.5)]
                  active:scale-[0.98]
                "
              >
                Send another
                <I.ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
            </article>
          </div>
        </PageContainer>
      </>
    );
  }

  return (
    <>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          {metadata.seo_desc && <meta name="description" content={metadata.seo_desc} />}
          {metadata.seo_keywords && <meta name="keywords" content={metadata.seo_keywords} />}
        </Helmet>
      )}

      <BackDropLoad wait={wait} />

      <PageContainer maxWidth="lg">

        {/* ═════ Hero Banner ═════ */}
        <section
          className="
            relative isolate overflow-hidden mt-3 mb-5
            rounded-3xl border border-amber/30 bg-surface-paper
          "
        >
          {/* amber radial */}
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10"
            style={{
              background: `
                radial-gradient(900px 380px at 8% -10%, rgba(251,191,36,0.18), transparent 55%),
                radial-gradient(700px 360px at 100% 110%, rgba(251,191,36,0.10), transparent 60%)
              `,
            }}
          />
          {/* subtle grid */}
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-30"
            style={{
              backgroundImage:
                'linear-gradient(rgba(46,46,52,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(46,46,52,0.6) 1px, transparent 1px)',
              backgroundSize: '52px 52px',
              maskImage: 'radial-gradient(ellipse 65% 70% at 50% 50%, #000, transparent 85%)',
              WebkitMaskImage: 'radial-gradient(ellipse 65% 70% at 50% 50%, #000, transparent 85%)',
            }}
          />
          <span aria-hidden className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-amber/60 to-transparent" />

          <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 md:gap-10 items-center px-5 sm:px-7 md:px-10 py-8 sm:py-10">
            <div className="min-w-0">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber/30 bg-amber/10 px-3 py-1.5">
                <span aria-hidden className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber opacity-60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber" style={{ boxShadow: '0 0 12px rgba(251,191,36,0.8)' }} />
                </span>
                <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-amber">Get in touch</span>
              </div>
              <h1 className="text-[28px] sm:text-[34px] md:text-[40px] font-bold leading-[1.05] tracking-[-0.03em] text-fg-primary">
                Talk to <span className="text-amber">us</span>.
              </h1>
              <div aria-hidden className="mt-3.5 h-[2px] w-12 rounded-full bg-gradient-to-r from-amber to-amber/0" />
              <p className="mt-3.5 max-w-[560px] text-[14px] leading-[1.65] text-fg-secondary">
                Questions about subscriptions, registration, or a listing? Send us a message and we'll respond promptly.
              </p>

              {/* tiny stat row */}
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2">
                <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber">
                  <I.Bolt className="h-3.5 w-3.5" />
                  1–2 business day response
                </span>
                <span aria-hidden className="hidden sm:inline-block h-3 w-px bg-amber/30" />
                <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber">
                  <I.ShieldCheck className="h-3.5 w-3.5" />
                  Secure &amp; confidential
                </span>
                <span aria-hidden className="hidden sm:inline-block h-3 w-px bg-amber/30" />
                <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber">
                  <I.Headset className="h-3.5 w-3.5" />
                  Real humans, every time
                </span>
              </div>
            </div>

            {/* Decorative icon block (desktop only) */}
            <div className="hidden md:flex relative h-20 w-20 shrink-0 items-center justify-center rounded-2xl border border-amber/40 bg-gradient-to-br from-amber/24 to-amber/8 shadow-[0_0_24px_-4px_rgba(251,191,36,0.35)]">
              <I.Headset className="h-9 w-9 text-amber" />
              <span aria-hidden className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber border-2 border-surface-paper">
                <span className="h-1.5 w-1.5 rounded-full bg-ink animate-pulse" />
              </span>
            </div>
          </div>
        </section>

        {/* ═════ Main grid: form + reach-us aside ═════ */}
        <section className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 md:gap-5 mb-10">

          {/* ─── Form Card ─── */}
          <div className="
            relative overflow-hidden rounded-2xl
            border border-amber/24 bg-surface-paper
            p-4 sm:p-6 md:p-7
          ">
            <span aria-hidden className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />

            {/* Card head */}
            <div className="mb-5">
              <div className="mb-1 inline-flex items-center gap-1.5">
                <I.Spark className="h-3.5 w-3.5 text-amber" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Send a message</span>
              </div>
              <h2 className="text-[18px] sm:text-[20px] font-bold tracking-[-0.018em] text-fg-primary">
                We'd love to hear from you
              </h2>
              <div aria-hidden className="mt-2 h-[2px] w-10 rounded-full bg-gradient-to-r from-amber to-amber/0" />
              <p className="mt-2 text-[12.5px] leading-[1.6] text-fg-secondary">
                Fill out the form below - fields marked <span className="text-amber font-semibold">*</span> are required.
              </p>
            </div>

            <form onSubmit={submit} noValidate className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="sm:col-span-2">
                <Field
                  label="Full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={error === 1}
                  errorMsg="Please enter your name."
                  required
                  icon={<I.Person className="h-4 w-4" />}
                  placeholder="What should we call you?"
                />
              </div>
              <Field
                label="Mobile"
                type="tel"
                value={mob}
                onChange={(e) => setMob(e.target.value.replace(/\D/g, '').slice(0, 10))}
                error={error === 2}
                errorMsg="Enter a valid 10-digit mobile number."
                required
                inputMode="numeric"
                maxLength={10}
                icon={<I.Phone className="h-4 w-4" />}
                placeholder="10-digit mobile"
              />
              <Field
                label="Email (optional)"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                icon={<I.Mail className="h-4 w-4" />}
                placeholder="you@example.com"
              />
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.14em] text-fg-tertiary">
                  Location <span className="ml-1 text-amber">*</span>
                </label>
                <AutoCompLoc compact loc={loc} setloc={setLoc} error={error === 5} required={false} />
                {error === 5 && <div className="mt-1.5 text-[11px] text-red-400">Please give your location.</div>}
              </div>
              <div className="sm:col-span-2">
                <Select
                  label="Subject"
                  value={sub}
                  onChange={(e) => setSub(e.target.value)}
                  error={error === 3}
                  errorMsg="Please select a subject."
                  required
                  placeholder="Choose an enquiry type…"
                  options={[
                    { value: '0', label: 'Choose an enquiry type…' },
                    ...subjects.map((s) => ({ value: s, label: s })),
                  ]}
                />
              </div>
              <div className="sm:col-span-2">
                <Field
                  label="Message"
                  multiline
                  rows={6}
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  error={error === 4}
                  errorMsg="Please enter your message."
                  required
                  placeholder="Tell us a bit about what you need help with…"
                />
              </div>

              <div className="sm:col-span-2 pt-1 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="inline-flex items-center gap-1.5 text-[11px] text-fg-tertiary">
                  <I.Lock className="h-3 w-3 text-amber/80" />
                  Your details are kept confidential.
                </p>
                <button
                  type="submit"
                  disabled={wait}
                  className="
                    group inline-flex h-12 items-center justify-center gap-1.5 rounded-lg px-6
                    text-[12.5px] font-bold uppercase tracking-[0.1em]
                    bg-amber text-ink
                    transition-[background-color,box-shadow,transform] duration-200 ease-out-soft
                    hover:bg-amber-light
                    hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_8px_22px_-8px_rgba(251,191,36,0.5)]
                    active:scale-[0.98]
                    disabled:opacity-60 disabled:pointer-events-none
                  "
                >
                  <span>{wait ? 'Sending…' : 'Send message'}</span>
                  <I.ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </button>
              </div>
            </form>
          </div>

          {/* ─── Reach-us aside ─── */}
          <aside className="flex flex-col gap-3 lg:gap-4">

            {/* Email cards */}
            <div className="
              relative overflow-hidden rounded-2xl
              border border-amber/30 bg-amber-gradient
              p-5
              shadow-[0_10px_28px_-12px_rgba(10,10,11,0.45)]
            ">
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-10 opacity-[0.08] mix-blend-multiply"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                }}
              />
              <span aria-hidden className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-ink/40 to-transparent" />

              <div className="mb-1 inline-flex items-center gap-1.5">
                <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-ink" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink/80">Reach us at</span>
              </div>
              <h3 className="text-[15px] font-bold tracking-[-0.01em] text-ink">Email support</h3>

              <div className="mt-3 space-y-2">
                <a
                  href="mailto:contact@esay.in"
                  className="
                    group flex items-center gap-2.5 rounded-lg px-3 py-2.5
                    border border-ink/15 bg-surface-paper/85
                    text-[13px] font-semibold text-fg-primary
                    transition-[background-color,border-color,transform] duration-200
                    hover:bg-surface-paper hover:border-amber/60 hover:-translate-y-0.5
                  "
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber/15 text-amber border border-amber/30">
                    <I.Mail className="h-3.5 w-3.5" />
                  </span>
                  <span className="flex-1 truncate">contact@esay.in</span>
                  <I.ArrowRight className="h-3.5 w-3.5 text-amber/60 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-amber" />
                </a>
                <a
                  href="mailto:contact@arawinz.com"
                  className="
                    group flex items-center gap-2.5 rounded-lg px-3 py-2.5
                    border border-ink/15 bg-surface-paper/85
                    text-[13px] font-semibold text-fg-primary
                    transition-[background-color,border-color,transform] duration-200
                    hover:bg-surface-paper hover:border-amber/60 hover:-translate-y-0.5
                  "
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber/15 text-amber border border-amber/30">
                    <I.Mail className="h-3.5 w-3.5" />
                  </span>
                  <span className="flex-1 truncate">contact@arawinz.com</span>
                  <I.ArrowRight className="h-3.5 w-3.5 text-amber/60 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-amber" />
                </a>
              </div>

              {/* Response window */}
              <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-ink/15 bg-surface-paper/85 px-3 py-2.5">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber/15 text-amber border border-amber/30">
                  <I.Clock className="h-3.5 w-3.5" />
                </span>
                <div>
                  <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-amber">Response time</div>
                  <p className="mt-0.5 text-[12.5px] leading-[1.5] text-fg-secondary">
                    We typically respond within <strong className="font-bold text-amber">1–2 business days</strong>.
                  </p>
                </div>
              </div>
            </div>

            {/* "What you can ask" mini-feature card */}
            <div className="
              relative overflow-hidden rounded-2xl
              border border-amber/22 bg-surface-paper
              p-5
            ">
              <span aria-hidden className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-amber/40 to-transparent" />
              <div className="mb-1 inline-flex items-center gap-1.5">
                <span aria-hidden className="inline-flex h-1 w-1 rounded-full bg-amber" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">What you can ask</span>
              </div>
              <h3 className="text-[14px] font-bold tracking-[-0.01em] text-fg-primary mb-3">Common topics</h3>
              <ul className="flex flex-col gap-1.5">
                {subjects.slice(0, 5).map((s) => (
                  <li key={s} className="flex items-start gap-2 text-[12.5px] leading-[1.5] text-fg-secondary">
                    <I.Check className="h-3 w-3 shrink-0 mt-1 text-amber" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </section>
      </PageContainer>
    </>
  );
};
